import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { formatDate } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, Container, Icon, Label, Popup } from "simplydo/react/ui";
import { Dropdown } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useTrayStatus } from "components/lib/Notifications";
import { Sticky } from "components/lib/UI";

import ImportWordModal from "./ImportWordModal";
import { useIdea } from "./hooks/useIdea";

const IdeaToolbarDiv = styled.div`
  min-height: ${({ theme }) => (theme.sizes.isMobile ? 30 : 40)}px;
  background-color: #f0f0f0;
  border-bottom: 1px solid lightgray;
  top: 50px;
  width: 100%;
`;

const ToolbarContainer = styled(Container)`
  padding: 5px 0 5px 0;
  display: flex;
  flex-direction: row-reverse !important;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const HorizontalDiv = styled.div<{
  $verticalAlign?: string;
  $horizontalAlign?: string;
}>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ $verticalAlign }) => $verticalAlign || "flex-start"};
  align-items: ${({ $horizontalAlign }) => $horizontalAlign || "center"};
  flex-wrap: wrap;
`;

type IdeaEditToolbarProps = {
  isEditing: boolean;
  undoChanges: () => void;
  redoChanges: () => void;
  canUndo: boolean;
  canRedo: boolean;
  savedAt: string | Date | "";
  deleteIdea: () => void;
  reloadIdea: () => void;
};

const IdeaEditToolbar = ({
  isEditing,
  undoChanges,
  redoChanges,
  canUndo,
  canRedo,
  savedAt,
  deleteIdea,
  reloadIdea,
}: IdeaEditToolbarProps) => {
  const [idea] = useIdea();
  const { t } = useTranslation();
  const theme = useTheme();
  const [_, setTrayStatus] = useTrayStatus();
  const user = useAppSelector((state) => state.user);
  const [importWordModalOpen, setImportWordModalOpen] = useState(false);

  const generatePDF = useCallback(() => {
    if (!user?._id || !idea?._id) {
      return;
    }
    const challengeName = idea?.ownerChallenge?.name || "Challenge";
    const displayName = `${challengeName} idea export`;

    api.users.createDownload(
      user._id,
      {
        downloadType: "ideaPdfExport",
        displayName: `${displayName}.pdf`,
        ideas: [idea?._id],
        challenge: idea?.ownerChallenge?._id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            ...prev,
            pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  }, [idea, user, setTrayStatus]);

  const downloadIdeaDocxReport = () => {
    if (!user?._id) {
      return;
    }
    const ideaName = idea?.name || "Idea";
    api.users.createDownload(
      user._id,
      {
        downloadType: "ideaDocxExport",
        displayName: `${ideaName} export.docx`,
        idea: idea?._id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            ...prev,
            pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  };
  const canExportWhileSubmitted = useMemo(() => {
    return (
      util.hasPermission(
        user,
        "org.manageChallenges",
        idea?.ownerChallenge?.organisation,
      ) || util.hasPermission(user, "challenge.editIdeas", idea?.challenge)
    );
  }, [user, idea]);

  const canImportFromWord = useMemo(
    () =>
      util.canEditIdea(user, idea) &&
      user?.ownerOrganisation?.superadminTags?.innovationType ===
        "externalInnovation",
    [user, idea],
  );
  if (!isEditing) return null;
  return (
    <Sticky top={theme.sizes.isMobile ? 90 : 100} style={{ zIndex: 90 }}>
      <ImportWordModal
        open={importWordModalOpen}
        onClose={() => setImportWordModalOpen(false)}
        ideaId={idea?._id}
        onImported={() => reloadIdea?.()}
      />
      <IdeaToolbarDiv>
        <ToolbarContainer>
          <HorizontalDiv $verticalAlign="space-between">
            <HorizontalDiv />
            <HorizontalDiv>
              <Popup
                content={t("ideas.undo")}
                on="hover"
                trigger={
                  <Button
                    size="mini"
                    icon="undo"
                    onClick={undoChanges}
                    disabled={!canUndo}
                    style={{ marginRight: 10 }}
                    basic
                  />
                }
              />
              <Popup
                content={t("ideas.redo")}
                on="hover"
                trigger={
                  <Button
                    size="mini"
                    icon="redo"
                    onClick={redoChanges}
                    disabled={!canRedo}
                    style={{ marginRight: 10 }}
                    basic
                  />
                }
              />
              <Dropdown
                icon={null}
                trigger={<Icon role="button" tabIndex={0} name="settings" />}
                direction="left"
                options={[
                  {
                    key: "export",
                    value: "export",
                    icon: "file pdf",
                    onClick: () => generatePDF(),
                    text: t("ideas.download.pdf"),
                  },

                  {
                    key: "export-word",
                    value: "export-word",
                    icon: "file word",
                    text: t("ideas.download.word"),
                    onClick: () => downloadIdeaDocxReport(),
                    disabled: idea?.isSubmitted && !canExportWhileSubmitted,
                  },
                  ...(canImportFromWord ?
                    [
                      {
                        key: "import-word",
                        value: "import-word",
                        icon: "upload",
                        content: (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {t("ideas.importWord.menuItem")}
                            <Label size="mini" color="violet">
                              {t("generic.beta")}
                            </Label>
                          </span>
                        ),
                        onClick: () => setImportWordModalOpen(true),
                        disabled: idea?.isSubmitted,
                      },
                    ]
                  : []),
                  ...((
                    !idea?.ownerChallenge?.preventIdeaCoverImages &&
                    idea?.coverImage
                  ) ?
                    [
                      {
                        key: "download-cover-image",
                        value: "download-cover-image",
                        icon: "download",
                        text: t("ideas.download.coverImage"),
                        onClick: () => {
                          if (idea?.coverImageUrl) {
                            window.open(
                              idea.coverImageUrl,
                              "_blank",
                              "noopener noreferrer",
                            );
                          }
                        },
                      },
                    ]
                  : []),
                  {
                    key: "delete-idea",
                    value: "delete-idea",
                    text: t("ideas.delete"),
                    icon: "trash",
                    onClick: () => deleteIdea(),
                  },
                ]}
              />
              {util.canEditIdea(user, idea) &&
                !idea?.isSubmitted &&
                !theme.sizes.isMobile && (
                  <Button
                    style={{ marginLeft: 10 }}
                    size="mini"
                    icon="save"
                    content={
                      savedAt ?
                        t("generic.savedAt", {
                          time: formatDate(savedAt, "HH:mm"),
                        })
                      : t("generic.save")
                    }
                    onClick={() => {
                      toast(t("ideas.autosave.warning"));
                    }}
                  />
                )}
            </HorizontalDiv>
          </HorizontalDiv>
        </ToolbarContainer>
      </IdeaToolbarDiv>
    </Sticky>
  );
};

export default IdeaEditToolbar;
